import React, { useEffect, useState } from 'react'
import { Modal, Checkbox, Table } from 'semantic-ui-react'
import { CustomerUserService } from 'utility/services/customer-user'
import { LoaderBar } from 'components'
import styled from 'styled-components'
import { X } from 'phosphor-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { GetTeamMemberList } from 'hooks'

const ScrollableContent = styled(Modal.Content)`
    max-height: 60vh;
    overflow-y: auto;

    .ui.table {
        border: none;
        margin-top: 0;
    }
`

export const CustomerUserAssignmentModal = ({ open, onClose, customerId, companyId }) => {
    const [assignedUsers, setAssignedUsers] = useState([])
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const { data: allUsers } = GetTeamMemberList()

    // Reset state when modal closes
    useEffect(() => {
        if (!open) {
            setAssignedUsers([])
            setSelectAll(false)
        }
    }, [open])

    useEffect(() => {
        if (open && customerId && companyId) {
            fetchAssignedUsers()
        }
    }, [open, customerId, companyId])

    const fetchAssignedUsers = async () => {
        try {
            setLoading(true)
            const response = await CustomerUserService.getList(companyId, customerId)

            // Get all user IDs that are assigned
            const assignedUserIds = (response || []).map(user => user.UserId || user.Id).filter(Boolean)

            // Map all users with their assignment status
            const usersWithAssignment = (allUsers || []).map(user => {
                const userId = user.UserId || user.unique_id || user.Id
                const assignedUser = (response || []).find(u => (u.UserId || u.Id) === userId)
                return {
                    UserId: userId,
                    UserName: user.Name || user.UserName || user.Email,
                    Email: user.Email,
                    IsSelected: assignedUserIds.includes(userId),
                    IsReadOnly: assignedUser?.IsReadOnly || false,
                    UniqueId: assignedUser?.UniqueId || assignedUser?.unique_id || null,
                }
            })

            setAssignedUsers(usersWithAssignment)
            const allSelected = usersWithAssignment.length > 0 && usersWithAssignment.every(u => u.IsSelected)
            setSelectAll(allSelected)
        } catch (error) {
            console.error('Error fetching assigned users:', error)
            // If API fails, still show all users as unselected
            const usersWithAssignment = (allUsers || []).map(user => ({
                UserId: user.UserId || user.unique_id || user.Id,
                UserName: user.Name || user.UserName || user.Email,
                Email: user.Email,
                IsSelected: false,
                IsReadOnly: false,
                UniqueId: null,
            }))
            setAssignedUsers(usersWithAssignment)
        } finally {
            setLoading(false)
        }
    }

    const handleSelectAll = () => {
        const newSelectAll = !selectAll
        setSelectAll(newSelectAll)
        setAssignedUsers(
            assignedUsers.map(user => ({
                ...user,
                IsSelected: newSelectAll,
            })),
        )
    }

    const handleUserSelect = userId => {
        const updatedUsers = assignedUsers.map(user =>
            user.UserId === userId
                ? {
                      ...user,
                      IsSelected: !user.IsSelected,
                      IsReadOnly: !user.IsSelected ? false : user.IsReadOnly,
                  }
                : user,
        )
        setAssignedUsers(updatedUsers)
        setSelectAll(updatedUsers.length > 0 && updatedUsers.every(u => u.IsSelected))
    }

    const handleReadOnlyToggle = userId => {
        setAssignedUsers(
            assignedUsers.map(user =>
                user.UserId === userId
                    ? { ...user, IsReadOnly: !user.IsReadOnly }
                    : user,
            ),
        )
    }

    const handleSave = async () => {
        try {
            setSaving(true)
            // Format selected user IDs as semicolon-separated string
            // Format: "101;205;330" (just user IDs)
            const selectedUserIds = assignedUsers
                .filter(user => user.IsSelected)
                .map(user => user.UserId)
                .join(';')

            const payload = {
                CompanyId: companyId,
                CustomerId: customerId,
                IsReadOnly: false, // Default value, can be adjusted per user if needed
                AssignedUsers: selectedUserIds,
            }

            await CustomerUserService.sync(payload)
            onClose(true)
        } catch (error) {
            console.error('Error saving user assignments:', error)
        } finally {
            setSaving(false)
        }
    }

    const handleClose = () => {
        setAssignedUsers([])
        setSelectAll(false)
        onClose(false)
    }

    return (
        <Modal open={open} onClose={handleClose} size="large">
            <Modal.Header>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>Assign Users to Customer</span>
                    <X className="pointer" size={32} onClick={handleClose} />
                </div>
            </Modal.Header>
            <ScrollableContent>
                {loading && <LoaderBar content="Loading" />}
                <Table>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>
                                <Checkbox
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                    label="Select All"
                                />
                            </Table.HeaderCell>
                            <Table.HeaderCell>User Name</Table.HeaderCell>
                            <Table.HeaderCell>Email</Table.HeaderCell>
                            <Table.HeaderCell textAlign="right" style={{ width: '120px' }}>IsReadOnly</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {assignedUsers && assignedUsers.length > 0 ? (
                            assignedUsers.map(user => (
                                <Table.Row key={user.UserId}>
                                    <Table.Cell collapsing>
                                        <Checkbox
                                            checked={user.IsSelected}
                                            onChange={() => handleUserSelect(user.UserId)}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>{user.UserName || 'Unnamed User'}</Table.Cell>
                                    <Table.Cell>{user.Email || '-'}</Table.Cell>
                                    <Table.Cell textAlign="right">
                                        <Checkbox
                                            toggle
                                            checked={user.IsReadOnly === true}
                                            onChange={() => handleReadOnlyToggle(user.UserId)}
                                            disabled={!user.IsSelected}
                                        />
                                    </Table.Cell>
                                </Table.Row>
                            ))
                        ) : (
                            <Table.Row>
                                <Table.Cell colSpan="4" textAlign="center">
                                    {loading ? 'Loading users...' : 'No users available'}
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </ScrollableContent>
            <Modal.Actions>
                <AppSecondaryButton onClick={handleClose} disabled={saving}>
                    Cancel
                </AppSecondaryButton>
                <AppPrimaryButton onClick={handleSave} disabled={saving || loading}>
                    {saving ? 'Saving...' : 'Save'}
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
